import { Pause, Play, RotateCcw, SkipBack, SkipForward } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const IconButton = ({ label, onClick, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    data-testid={testid}
    className="inline-flex h-10 w-10 items-center justify-center rounded-full text-sand/80 transition-colors hover:bg-sand/10 hover:text-sand"
  >
    {children}
  </button>
);

/** Presenter transport for the demo reels: restart · previous act · play/pause · next act · scrubbable act timeline · clock. */
export const DemoPlayerBar = ({ acts, totalSeconds, timeline, testid }) => {
  const { elapsed, playing, ended, currentAct, toggle, restart, seek, nextAct, prevAct } = timeline;
  const pct = Math.min(100, (elapsed / totalSeconds) * 100);

  const onTrackClick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    seek(((e.clientX - r.left) / r.width) * totalSeconds);
  };

  return (
    <div
      data-testid={`${testid}-player`}
      role="group"
      aria-label="Demo playback controls"
      className="fixed bottom-5 left-1/2 z-30 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-1 rounded-full bg-ink/95 py-2 pl-2 pr-4 text-sand shadow-2xl ring-1 ring-sand/10 backdrop-blur lg:bottom-8 lg:gap-2"
    >
      <IconButton label="Restart (R)" onClick={restart} testid={`${testid}-restart`}>
        <RotateCcw className="h-4 w-4" strokeWidth={2.2} />
      </IconButton>
      <IconButton label="Previous step (←)" onClick={prevAct} testid={`${testid}-prev`}>
        <SkipBack className="h-4 w-4" strokeWidth={2.2} />
      </IconButton>
      <button
        type="button"
        onClick={toggle}
        data-testid={`${testid}-play-toggle`}
        aria-label={ended ? "Replay" : playing ? "Pause" : "Play"}
        title={`${ended ? "Replay" : playing ? "Pause" : "Play"} (Space)`}
        className="mx-1 inline-flex h-12 w-12 items-center justify-center rounded-full bg-yellow text-ink shadow-md transition-transform hover:scale-105 active:scale-95"
      >
        {ended ? (
          <RotateCcw className="h-5 w-5" strokeWidth={2.4} />
        ) : playing ? (
          <Pause className="h-5 w-5" strokeWidth={2.4} />
        ) : (
          <Play className="h-5 w-5 translate-x-[1px]" strokeWidth={2.4} />
        )}
      </button>
      <IconButton label="Next step (→)" onClick={nextAct} testid={`${testid}-next`}>
        <SkipForward className="h-4 w-4" strokeWidth={2.2} />
      </IconButton>

      <div className="ml-2 flex items-center gap-3">
        <div
          role="slider"
          aria-label="Timeline"
          aria-valuemin={0}
          aria-valuemax={totalSeconds}
          aria-valuenow={Math.round(elapsed)}
          tabIndex={-1}
          onClick={onTrackClick}
          data-testid={`${testid}-timeline`}
          className="relative h-6 w-32 cursor-pointer sm:w-56 lg:w-72"
        >
          <span className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-sand/20" />
          <span className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-yellow" style={{ width: `${pct}%` }} />
          {acts.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={(e) => { e.stopPropagation(); seek(a.startAt); }}
              aria-label={`Step ${a.id}: ${a.title}`}
              title={`${a.id} · ${a.title}`}
              data-testid={`${testid}-marker-${a.id}`}
              className={`absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ink transition-transform hover:scale-125 ${
                a.id === currentAct.id ? "bg-sand" : a.startAt <= elapsed ? "bg-yellow" : "bg-sand/40"
              }`}
              style={{ left: `${(a.startAt / totalSeconds) * 100}%` }}
            />
          ))}
        </div>
        <span data-testid={`${testid}-clock`} className="hidden font-mono text-[13px] tabular-nums text-sand/80 sm:inline">
          {fmt(elapsed)} / {fmt(totalSeconds)}
        </span>
      </div>
    </div>
  );
};
